import Validator from "./Validator";
import TaskStatuses from "../enums/taskStatuses";
import arrayUtils from "./arrayUtils";

interface TaskItem {
  [key: string]: unknown;
}

interface TaskPoolInterface {
  init: (config: ConfigParams) => void;
  push: (id: string, item: any) => void;
  cancelTask: (taskId: string) => void;
  runningTasksCount: () => number | undefined;
}

interface TaskData {
  status: number;
  item: TaskItem | File | string | number | boolean;
}

interface Task {
  [id: string]: TaskData;
}

interface TaskInfo {
  id: string;
  status: number | undefined;
}

export interface ConfigParams {
  concurrentItemsCount: number;
  runningTasksCount: number;
  tasks: Task;
  onTaskQueued?: (task: TaskInfo, item: any) => void;
  onTaskCanceled?: (taskId: string) => void;
  taskAction: (id: string, item: any) => Promise<unknown>;
}

let configuration: ConfigParams | null = null;

const initValidationSchema = {
  concurrentItemsCount: Validator.create().isPositiveInteger(),
  taskAction: Validator.create().isFunction(),
};

const pushValidationSchema = {
  id: Validator.create().isNotNullOrUndefined(),
  item: Validator.create().isNotNullOrUndefined(),
};

const run = async (config: ConfigParams): Promise<void> => {
  const { concurrentItemsCount, runningTasksCount } = config;
  let availableCount = concurrentItemsCount - runningTasksCount;
  if (availableCount <= 0) {
    return;
  }

  const keys = Object.keys(config.tasks);

  for (let index = 0; index < keys.length && availableCount > 0; ++index) {
    let id = keys[index];
    let task = config.tasks[id];
    if (task.status === TaskStatuses.queued) {
      task.status = TaskStatuses.inProgress;
      --availableCount;
      ++config.runningTasksCount;
      config
        .taskAction(id, task.item)
        .then(() => handleTaskCompletion(id, config))
        .catch(() => handleTaskCompletion(id, config));
    }
  }
};

const handleTaskCompletion = (id: string, config: ConfigParams): void => {
  delete config.tasks[id];
  --config.runningTasksCount;
  run(config);
};

const getRunningTasksCount = (tasks: Task): number => {
  return arrayUtils.count(Object.values(tasks), (item) => item.status === TaskStatuses.inProgress);
};

const onTaskQueued = (config: ConfigParams, id: string, item: TaskItem | File | string | number | boolean): void => {
  config.onTaskQueued && config.onTaskQueued({ id, status: TaskStatuses.queued }, item as any);
};

const taskPool: TaskPoolInterface = {
  init: (config: ConfigParams): void => {
    Validator.validateSchemaAndThrow(initValidationSchema, config);

    if (configuration) {
      configuration.tasks = Object.assign(configuration.tasks, config.tasks);
    } else {
      configuration = config;
    }

    if (!configuration.tasks) {
      configuration.tasks = {};
    }

    configuration.runningTasksCount = getRunningTasksCount(configuration.tasks);
    run(configuration);
  },

  push(id: string, item: TaskItem | File): void {
    Validator.validateSchemaAndThrow(pushValidationSchema, { id, item });
    if (!configuration) {
      return;
    }

    const existingTask = configuration.tasks[id];
    if (existingTask) {
      throw new Error("Task with the same id already exists");
    }

    configuration.tasks[id] = {
      item,
      status: TaskStatuses.queued,
    };

    onTaskQueued(configuration, id, item as TaskItem);

    run(configuration);
  },

  cancelTask(taskId: string): void {
    if (!configuration) {
      return;
    }

    const task = configuration.tasks[taskId];
    if (task && task.status === TaskStatuses.queued) {
      delete configuration.tasks[taskId];
      configuration.onTaskCanceled && configuration.onTaskCanceled(taskId);
    }
  },

  runningTasksCount(): number | undefined {
    if (!configuration) {
      return undefined;
    }
    return getRunningTasksCount(configuration.tasks);
  },
};

export default taskPool;
